import { useQuery, useQueryClient } from '@tanstack/react-query';

interface ShopifyVariant {
  id: number;
  title: string;
  product_id: number;
  price: string;
  sku?: string;
  available: boolean;
  name?: string;
  public_title?: string;
}

interface ShopifyProduct {
  id: number;
  title: string;
  handle: string;
  variants: ShopifyVariant[];
  images: string[];
}

interface ShopifyVariantLookupResult {
  productTitle: string;
  variantTitle: string;
  price: string | null;
  compareAtPrice: string | null;
  fetchedAt: number;
}

// Global cache to avoid duplicate fetches
const variantCache = new Map<number, ShopifyVariantLookupResult>();
const CACHE_TTL = 1000 * 60 * 60; // 1 hour cache

/**
 * Fetch product/variant data from Shopify Storefront API /cart/add.js endpoint
 * This endpoint works without auth and returns full product+variant data
 */
async function fetchShopifyVariant(variantId: number): Promise<ShopifyVariantLookupResult> {
  // Check cache first
  const cached = variantCache.get(variantId);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
    return cached;
  }

  const storeUrl = import.meta.env.VITE_SHOPIFY_STORE_URL || window.location.origin;

  // Use Storefront API GraphQL (works without auth for public data)
  const graphqlQuery = JSON.stringify({
    query: `
      query GetVariantTitle($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            title
            priceV2 {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            product {
              title
            }
          }
        }
      }
    `,
    variables: {
      id: `gid://shopify/ProductVariant/${variantId}`
    }
  });

  const response = await fetch(`${storeUrl}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: graphqlQuery
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch variant ${variantId}`);
  }

  const data = await response.json();

  const result: ShopifyVariantLookupResult = {
    productTitle: data?.data?.node?.product?.title || 'Unknown Product',
    variantTitle: data?.data?.node?.title || 'Unknown Variant',
    price: data?.data?.node?.priceV2?.amount ?? null,
    compareAtPrice: data?.data?.node?.compareAtPrice?.amount ?? null,
    fetchedAt: Date.now(),
  };

  // Store in cache
  variantCache.set(variantId, result);

  return result;
}

/**
 * Hook for looking up Shopify variant product titles
 * Handles loading states and caching
 */
export function useShopifyVariantLookup(variantId: number | undefined | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['shopify-variant', variantId],
    queryFn: () => fetchShopifyVariant(variantId!),
    enabled: !!variantId && !variantCache.has(variantId),
    staleTime: CACHE_TTL,
    cacheTime: CACHE_TTL,
    retry: 1,
    retryDelay: 1000,
    initialData: () => {
      // Return cached data immediately if available
      if (variantId && variantCache.has(variantId)) {
        return variantCache.get(variantId);
      }
      return undefined;
    },
  });
}

/**
 * Prefetch multiple variants at once
 */
export function prefetchShopifyVariants(variantIds: number[], queryClient: any) {
  return Promise.allSettled(
    variantIds.map(id =>
      queryClient.prefetchQuery({
        queryKey: ['shopify-variant', id],
        queryFn: () => fetchShopifyVariant(id),
        staleTime: CACHE_TTL,
      })
    )
  );
}
